'use client'

import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useAuthStore } from '@/store/auth.store'
import { useThemeStore } from '@/store/theme.store'
import { ConfirmDialogRoot, PromptDialogRoot } from '@/components/ui/confirm-dialog'

function CookieSync() {
  const { isAuthenticated, user } = useAuthStore()

  // Ressincrona o cookie strada_role a partir do estado persistido no localStorage.
  // Resolve o loop de redirect quando o cookie expira mas o Zustand ainda tem sessão.
  useEffect(() => {
    if (isAuthenticated && user?.role) {
      document.cookie = `strada_role=${user.role}; path=/; max-age=604800; SameSite=Lax`
    }
  }, [isAuthenticated, user?.role])

  return null
}

// Garante que queries estáticas são refetchadas ao navegar entre páginas.
// Necessário porque o Next.js 15 pode restaurar páginas do router cache
// sem re-montar os componentes React, impedindo o refetchOnMount do React Query.
function QueryRefreshOnNavigate() {
  const pathname = usePathname()
  const qc = useQueryClient()

  useEffect(() => {
    qc.refetchQueries({ type: 'active', stale: true })
  }, [pathname, qc])

  return null
}

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useThemeStore()

  // Sincroniza o atributo data-theme sempre que o tema muda
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  return <>{children}</>
}


export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0,
            gcTime: 1000 * 60 * 5,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CookieSync />
        <QueryRefreshOnNavigate />
        {children}
        <ConfirmDialogRoot />
        <PromptDialogRoot />
      </ThemeProvider>
    </QueryClientProvider>
  )
}
